import React, { useEffect } from "react"
import { Tabs, Tab, Box, useMediaQuery, LinearProgress } from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import useStore from "src/v2/utils/store"
import { graphIcon } from "src/components/icons-svg"
import useASTStore from "src/v2/utils/AST-store"

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: 20,
    [theme.breakpoints.down("sm")]: {
      paddingTop: 10,
      borderRadius: 0,
      boxShadow: "none",
    },
  },
  tabs: {
    minHeight: 40,
    "& .MuiTab-root": {
      minHeight: 40,
      minWidth: 80,
      fontWeight: 500,
      fontSize: 15,
      textTransform: "none",
      [theme.breakpoints.down("sm")]: {
        minWidth: 60,
        fontSize: 13,
      },
    },
    "& .Mui-selected": {
      background: "#ffceb4",
      borderRadius: 8,
    },
  },
  tabLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  progressBox: {
    marginTop: 2,
    marginBottom: -6,
    width: "100%",
  },
  progress: {
    height: 5,
    borderRadius: 3,
    backgroundColor: "#e3e3e3",
    "& .MuiLinearProgress-barColorPrimary": {
      backgroundColor: "#1a90ff",
    },
  },
}));

export default function ResourceTabs() {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const tabsArray = useStore((state) => state.oneReview?.analyzedData?.json?.tabsArray || [])
  const selectedResourseType = useASTStore((s) => s.selectedResourseType)
  const setSelectedResourceType = useASTStore((s) => s.setSelectedResourceType)
  const getProgressForTab = useASTStore((s) => s.getProgressForTab)
  const completedTasks = useStore((s) => s.completedTasks)

  // Select first tab by default if none is selected
  useEffect(() => {
    if (!selectedResourseType && tabsArray.length > 0) {
      setSelectedResourceType(tabsArray[0].id)
    }
  }, [selectedResourseType, tabsArray])

  // Re-run progress calculation when completedTasks changes
  useEffect(() => {
    // This will trigger a re-render and update progress bars
    // No-op body, just dependency on completedTasks
  }, [completedTasks])

  const showProgressCheckboxFormalMapping = window["config"]?.SHOW_PROGRESS_CHECKBOX_FORMAL_MAPPING === "YES"

  if (!tabsArray.length) {
    return (
      <Box p={2} textAlign="center" color="textSecondary">
        No resource types available.
      </Box>
    )
  }

  return (
    <Box className={classes.root}>
      {!selectedResourseType && (
        <Box mb={2} color="textSecondary" fontWeight={500} fontSize={16}>
          Choose a resource type:
        </Box>
      )}
      <Tabs
        value={selectedResourseType || false}
        onChange={(_, val) => {
          setSelectedResourceType(val)
        }}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
        variant={isMobile ? "scrollable" : "standard"}
        scrollButtons={isMobile ? "auto" : "off"}
        aria-label="Resource Tabs"
      >
        {tabsArray.map((tab) => {
          const isSelected = tab.id === selectedResourseType
          const progressForTab = getProgressForTab(tab.id) || { completed: 0, total: 0 }
          const percent = progressForTab.total
            ? Math.floor((progressForTab.completed / progressForTab.total) * 100)
            : 0

          return (
            <Tab
              key={tab.id}
              value={tab.id}
              label={
                <div className={classes.tabLabel}>
                  {tab.icon === "graph" ? (
                    <span style={{ display: "flex", alignItems: "center" }}>{graphIcon}</span>
                  ) : null}
                  <span>{tab.name}</span>
                </div>
              }
              wrapped
              disableRipple
              style={{
                fontWeight: isSelected ? 600 : 400,
                background: isSelected ? "#C6DDFC" : "#f5f5f5", // <-- updated colors
                borderRadius: 8,
                marginRight: 8,
                minWidth: 80,
                cursor: isSelected ? "default" : "pointer",
                transition: "background 0.2s",
              }}
              icon={
                showProgressCheckboxFormalMapping && (
                  <Box className={classes.progressBox}>
                    <LinearProgress
                      variant="determinate"
                      value={percent}
                      className={classes.progress}
                    />
                  </Box>
                )
              }
            />
          )
        })}
      </Tabs>
    </Box>
  )
}